import React, { useState, useEffect, useMemo } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import { useInsightsWs } from "../Utils/CoTraderUtils";

const data = {
  current_price: 149.485,
  currency_pair: "USDJPY",
  insights: [
    "USDJPY trading around 149.4850, showing stability in the short term.",
    "Key resistance at 150.00, support at 149.00 based on recent price data.",
    "Economic factors suggest potential USD strength amid upcoming events.",
    "Consider short-term long positions near support levels.",
    "Monitor key economic events for potential market volatility.",
  ],
};

const CoTraderInsightsWidget = () => {
  const [currencyPair, setCurrencyPair] = useState("USDJPY");
  const userLanguage = localStorage.getItem("user_language") || "English";
  const theme = useTheme();
  const colors = {
    dark: theme.globallink.colors.Black[100],
    background: theme.globallink.colors.Grey[1100],
    containerBackground: theme.globallink.colors.DarkGrey[500],
    titleBackground: theme.globallink.colors.White[500],
    textPrimary: theme.globallink.colors.Blue[500],
    textSecondary: theme.globallink.colors.Grey[600],
    border: theme.globallink.colors.Slate[700],
    white: theme.globallink.colors.White[100],
  };

  const useStyles = makeStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      height: "100%",
      overflow: "hidden",
      paddingLeft: "10px",
      paddingRight: "10px",
      backgroundColor: theme.globallink.colors.DarkGrey[500],
    },
    title: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
    },
    currencyPair: {
      fontSize: "calc(1.5vw + 10px)", // Responsive size that scales with the viewport width
      color: colors.titleBackground,
      '@media (max-width: 768px)': { // Adjust for smaller screens
        fontSize: "18px",
      },
      '@media (max-width: 480px)': { // Adjust for very small screens
        fontSize: "16px",
      },
    },
    price: {
      fontSize: "calc(1.5vw + 10px)", // Responsive size that scales with the viewport width
      color: colors.white,
      '@media (max-width: 768px)': { // Adjust for smaller screens
        fontSize: "18px",
      },
      '@media (max-width: 480px)': { // Adjust for very small screens
        fontSize: "16px",
      },
    },
    insights: {
      color: colors.white,
      fontSize: "calc(1vw + 8px)", // Responsive size for insights
      textAlign: "center",
    },
    dotsContainer: {
      display: "flex",
      justifyContent: "center",
      marginTop: "15px",
    },
    dot: {
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      backgroundColor: colors.textSecondary,
      margin: "0 5px",
    },
    activeDot: {
      backgroundColor: colors.textPrimary,
    },
  });

  const classes = useStyles();
  const [activeIndex, setActiveIndex] = useState(0);
  const { insights, currentPrice } = useInsightsWs({
    currencyPair,
    updateSpeed: 3,
    userLanguage,
    showCountDownInsights: true,
  });
  const insightTexts = useMemo(() => {
    if (insights.length === 0) return data.insights;

    return insights.map((insight: any) => {
      const title = String(insight?.title || "").trim();
      const description = String(insight?.description || "").trim();

      if (title && description) return `${title}: ${description}`;
      if (title) return title;
      if (description) return description;
      return "Generating the Updates...";
    });
  }, [insights]);

  // Auto-scroll functionality using useEffect and setInterval
  useEffect(() => {
    if (insightTexts.length === 0) return undefined;

    const interval = setInterval(() => {
      setActiveIndex((prevIndex) =>
        prevIndex === insightTexts.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, [insightTexts.length]);

  useEffect(() => {
    if (activeIndex >= insightTexts.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, insightTexts.length]);

  useEffect(() => {
    let previousValue = localStorage.getItem('EMALPHA_DASHBAR_CURRENCY_PAIR');
  
    const interval = setInterval(() => {
      const currentValue: string | null = localStorage.getItem('EMALPHA_DASHBAR_CURRENCY_PAIR');
  
      if (currentValue !== previousValue) {
        setCurrencyPair(currentValue || ""); // Use an empty string as a fallback if currentValue is null
        previousValue = currentValue;
      }
    }, 1000);
  
    // Cleanup the interval on component unmount
    return () => clearInterval(interval);
  }, []);
  



  return (
    <div className={classes.container}>
      <div className={classes.title}>
        <div className={classes.currencyPair}>{currencyPair}</div>
        <div className={classes.price}>
          {currentPrice == null ? "--" : currentPrice}
        </div>
      </div>
      <div className={classes.insights}>{insightTexts[activeIndex]}</div>
      <div className={classes.dotsContainer}>
        {insightTexts.map((_, index) => (
          <div
            key={index}
            className={`${classes.dot} ${
              index === activeIndex ? classes.activeDot : ""
            }`}
            onClick={() => setActiveIndex(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default CoTraderInsightsWidget;
